'use client';
import React from 'react';
import { Input } from 'antd';
import { SessionContext } from '@benzinga/session-context';
import styled from '@benzinga/themetron';
import { Note, NotesManager } from '@benzinga/notes-manager';
import { isString } from '@benzinga/utils';
import { TickerContext } from '../context';
import { TextAreaRef } from 'antd/lib/input/TextArea';
import { PermissionedComponent } from '@benzinga/user-context';
import { TrackingManager } from '@benzinga/tracking-manager';
import { StockSymbol } from '@benzinga/session';
import { ProUserSettingsManager } from '@benzinga/pro-user-settings';

type TextInputElement = HTMLInputElement | HTMLTextAreaElement;
interface Props {
  symbol?: StockSymbol;
}
export const TickerNotesModule = (props: Props) => (
  <PermissionedComponent permission={{ action: 'bzpro/widget/use', resource: 'watchlist' }}>
    {access => (access ? <TickerNotes {...props} /> : null)}
  </PermissionedComponent>
);

export const TickerNotes: React.FC<Props> = props => {
  const session = React.useContext(SessionContext);
  const ticker = React.useContext(TickerContext);
  const proUserSettings = session.getManager(ProUserSettingsManager).getSettings();
  const [notesState, setNotesState] = React.useState<{
    newText: string;
    notes: Note[];
    placeholder: string;
  }>({
    newText: '',
    notes: [],
    placeholder: `Type a new note...`,
  });
  const textAreaRef = React.useRef<TextAreaRef>(null);
  const addNoteButtonJustClicked = React.useRef<boolean>(false);
  const [isAddNoteFocused, setIsAddNoteFocused] = React.useState<boolean>(proUserSettings?.autoFocusOnTickerHover);
  const notesManager = session.getManager(NotesManager);
  const trackingManager = session.getManager(TrackingManager);
  const symbol = React.useMemo(() => props?.symbol ?? ticker.symbol, [props?.symbol, ticker.symbol]);

  React.useEffect(() => {
    (async () => {
      const { ok: notes = [] } = await notesManager.getNotesFiltered(symbol);
      setNotesState(oldState => ({
        ...oldState,
        notes,
      }));
    })();
  }, [notesManager, symbol, ticker.symbol]);

  React.useEffect(() => {
    if (isAddNoteFocused && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [isAddNoteFocused]);

  const onFocus = (event: React.FocusEvent<TextInputElement>) => {
    setIsAddNoteFocused(true);
    ticker.setDialogMode(true);
    const targetLength = event.target.value.length ?? 0;
    event.target.setSelectionRange(targetLength, targetLength); //to stick cursor at the end of current value
  };

  const onBlur = (event: React.FocusEvent<HTMLElement>) => {
    const field = event.target;
    if (!field.isConnected || document.activeElement === field) {
      return;
    }

    if (addNoteButtonJustClicked.current) {
      addNoteButtonJustClicked.current = false;
      if (isAddNoteFocused && textAreaRef.current) {
        textAreaRef.current.focus();
      }
    }
    setIsAddNoteFocused(false);
    ticker.setDialogMode(false);
  };

  const onNewNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e?.target?.value;
    addNoteButtonJustClicked.current = true;
    if (isString(val)) {
      setNotesState(oldState => ({ ...oldState, newText: val }));
      ticker.updateHeight();
    }
  };

  const onNewNote = React.useCallback(async () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setNotesState(oldState => {
      if (oldState.newText.length > 0) {
        (async () => {
          await notesManager.createNote(symbol, notesState.newText);
          const { ok: notes = [] } = await notesManager.getNotesFiltered(symbol);
          trackingManager.trackNoteEvent('add', {
            symbol,
          });
          setNotesState(oldState => ({
            ...oldState,
            notes,
          }));
          ticker.updateHeight();
        })();
        return {
          ...oldState,
          newText: '',
          placeholder: `Type a new note...`,
        };
      } else {
        ticker.updateHeight();
        return {
          ...oldState,
          placeholder: 'Note cannot be empty',
        };
      }
    });
  }, [notesManager, notesState.newText, symbol, ticker, trackingManager]);

  const onKeyStroke = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement> | undefined) => {
      if (e?.key === 'Enter') {
        textAreaRef.current?.blur();
        onNewNote();
        return;
      }
      return;
    },
    [onNewNote],
  );
  return (
    <>
      <NoteHeaderDiv>
        <LatestNotesDiv>Latest Note</LatestNotesDiv>
      </NoteHeaderDiv>
      {notesState.notes?.[0]?.getText()?.length > 0 && <NoteBodyDiv>{notesState.notes?.[0]?.getText()}</NoteBodyDiv>}
      <NotesAddDiv>
        <NoteAddBorderDiv>
          <RowContainerDiv>
            {isAddNoteFocused ? (
              <Input.TextArea
                autoFocus={proUserSettings?.autoFocusOnTickerHover}
                autoSize={true}
                onBlur={onBlur}
                onChange={onNewNoteChange}
                onFocus={onFocus}
                onKeyDown={onKeyStroke}
                placeholder={notesState.placeholder}
                ref={textAreaRef}
                value={notesState.newText}
              />
            ) : (
              <Input
                autoFocus={proUserSettings?.autoFocusOnTickerHover}
                onBlur={onBlur}
                onFocus={onFocus}
                placeholder={notesState.placeholder}
                value={notesState.newText}
              />
            )}
            <AddButtonContainerDiv>
              <AddButtonDiv onClick={onNewNote}>Add</AddButtonDiv>
            </AddButtonContainerDiv>
          </RowContainerDiv>
        </NoteAddBorderDiv>
      </NotesAddDiv>
    </>
  );
};

const NoteHeaderDiv = styled.div``;

const NoteBodyDiv = styled.div`
  margin: 5px 12px;
  max-width: 350px;
  white-space: normal;
`;

const LatestNotesDiv = styled.div`
  font-size: 1.2em;
  margin: 5px 12px;
`;

const NotesAddDiv = styled.div`
  margin-top: 2px;
`;

const NoteAddBorderDiv = styled.div`
  border-bottom-width: 1px;
  border-color: ${props => props.theme.colors.border};
  border-style: solid;
  margin-bottom: 5px;
  padding: 5px;
`;

const RowContainerDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const AddButtonDiv = styled.div`
  background: ${props => props.theme.colors.brand};
  cursor: pointer;
  margin-bottom: 3px;
  margin-left: 4px;
  padding-bottom: 6px;
  padding-left: 10px;
  padding-right: 10px;
  padding-top: 6px;
`;

const AddButtonContainerDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;
